/**
 * Excel Parser Service
 * Service for reading and extracting data from Excel files for audit data upload
 */

import * as ExcelJS from 'exceljs';
import type {
  AuditGeneralData,
  ProducerData,
  PlotData,
  ValidationResult,
  CropData,
  ControlPointData,
  SampleData,
  BlockData,
} from './types';

// Constants matching backend row numbers
const DATE_AUDIT_ROW = 3;
const START_TIME_ROW = 4;
const FINISH_TIME_ROW = 5;
const PERSON_IN_CHARGE_OF_RECEIVING_ROW = 6;
const NIF_PERSON_IN_CHARGE_RECEIVING_ROW = 7;
const PHONE_PERSON_IN_CHARGE_ROW = 8;
const ROLE_IN_COMPANY_PERSON_IN_CHARGE_ROW = 9;
const AUDITOR_ROW = 10;
const AUDITOR_NIF_ROW = 11;
const AUDITOR_PHONE_ROW = 12;
const COMMENTS_ROW = 15;

// Complete audit constants
const AUDIT_CODE_COMPLETE_ROW = 3;
const FIELD_NAME_COMPLETE_ROW = 4;
const CONTACT_COMPLETE_ROW = 5;
const PHONE_CONTACT_COMPLETE_ROW = 6;
const EMAIL_CONTACT_COMPLETE_ROW = 7;
const EXPORTER_COMPLETE_ROW = 10;
const PACK_HOUSE_COMPLETE_ROW = 11;
const TOTAL_SURFACE_COMPLETE_ROW = 14;
const CHECK_MAP_COMPLETE_ROW = 15;
const ESTIMATED_YIELD_COMPLETE_ROW = 16;
const HARVEST_DATE_COMPLETE_ROW = 17;
const PERCENTAGE_DECREASE_COMPLETE_ROW = 18;
const REASON_DECREASE_COMPLETE_ROW = 19;
const UNDERCOVER_FIELD = 20;
const ECO_FIELD = 21;
const NUMBER_OF_CROPS_COMPLETE_ROW = 24;
const NUMBER_OF_CONTROL_POINTS_COMPLETE_ROW = 37;
const NAME_FIRST_CONTROL_POINT_COMPLETE_ROW = 39;

// Yield audit constants
const AUDIT_CODE_YIELD_ROW = 3;
const FIELD_NAME_YIELD_ROW = 4;
const EXPORTER_YIELD_ROW = 10;
const PACK_HOUSE_YIELD_ROW = 11;
const ESTIMATED_YIELD_YIELD_ROW = 12;
const HARVEST_DATE_YIELD_ROW = 13;
const PERCENTAGE_LOOSE_YIELD_ROW = 14;
const REASON_LOOSE_YIELD_ROW = 15;
const UNDERCOVER_FIELD_YIELD = 16;
const ECO_FIELD_YIELD = 17;
const NUMBER_OF_CONTROL_POINTS_YIELD_ROW = 20;

// Plot constants (Complete)
const NUMBER_PLOTS_TANGO_ROW = 3;
const NAME_OF_PLOTS_COMPLETE_ROW = 4;
const PLOT_NAME_COMPLETE_ROW = 3;
const PLANTATION_TYPE_COMPLETE_ROW = 4;
const ROOTSTOCK_COMPLETE_ROW = 5;
const SURFACE_COMPLETE_ROW = 6;
const SPACING_ROW_COMPLETE_ROW = 8;
const SPACING_COLUMN_COMPLETE_ROW = 9;
const CROP_DATE_COMPLETE_ROW = 10;
const NUM_PLANTS_COMPLETE_ROW = 11;
const WOODSTOCK_COMPLETE_ROW = 12;
const NUM_GRAFTS_COMPLETE_ROW = 13;
const DOUBLED_WITH_COMPLETE_ROW = 14;
const YIELD_COMPLETE_ROW = 15;
const AVG_CALIBER_COMPLETE_ROW = 16;
const BRIX_COMPLETE_ROW = 19;
const ACIDITY_COMPLETE_ROW = 20;
const ILLEGAL_PLOT_COMPLETE_ROW = 21;
const EXTERNAL_DAMAGE_COMPLETE_ROW = 25;
const START_BLOCK_COMPLETE_ROW = 29;
const NUMBER_OF_SAMPLES_COMPLETE_ROW = 37;

// Plot constants (Yield)
const PLOT_NAME_YIELD_ROW = 3;
const ROOTSTOCK_YIELD_ROW = 4;
const WOODSTOCK_YIELD_ROW = 5;
const NUM_GRAFTS_YIELD_ROW = 6;
const YIELD_ROW = 7;
const AVG_CALIBER_YIELD_ROW = 9;
const SPACING_YIELD_ROW = 10;
const SURFACE_YIELD_ROW = 11;
const NUM_PLANTS_YIELD_ROW = 12;
const CROP_YEAR_YIELD_ROW = 13;
const DOUBLED_WITH_YIELD_ROW = 14;
const BRIX_YIELD_ROW = 16;
const ACIDITY_YIELD_ROW = 17;
const EXTERNAL_DAMAGE_YIELD_ROW = 18;
const START_BLOCK_YIELD = 22;
const NUMBER_OF_SAMPLES_YIELD_ROW = 29;

export class ExcelParserService {
  /**
   * Detects the audit type based on the Excel structure
   * @param workbook - The Excel workbook
   * @returns Audit type (1 = Complete, 2 = Yield)
   */
  detectAuditType(workbook: ExcelJS.Workbook): number {
    console.log('Detecting audit type from Excel structure...');
    
    // Strategy 1: Check if "Datos Generales Tango" worksheet exists (only in Complete audits)
    const tangoSheet = this.findWorksheetByNames(workbook, [
      'Datos Generales Tango',
      'Datos Parcelas',
      'Dati Generali Tango',
      'Dati Parcelle',
      'General Tango Data',
      'Plot Data'
    ]);
    
    if (tangoSheet) {
      console.log('Found "Datos Generales Tango" sheet -> Complete Audit (Type 1)');
      return 1; // Complete audit
    }
    
    // Strategy 2: Check producer data sheet for specific fields
    const producerSheet = this.findWorksheetByNames(workbook, [
      'Datos Productor',
      'Datos Generales Productor',
      'Dati generali Produttore',
      'Dati Produttore',
      'Producer Data',
      'General Producer Data'
    ]) || workbook.worksheets[1];
    
    if (producerSheet) {
      // Complete audits have "Contacto" field in row 5
      // Yield audits don't have this field
      const contactField = this.getStrValue(producerSheet, CONTACT_COMPLETE_ROW, 2);
      
      // Complete audits have "Número de cultivos" field around row 24
      const numCropsValue = this.getCellValue(producerSheet.getCell(NUMBER_OF_CROPS_COMPLETE_ROW, 2));
      
      if (contactField || (numCropsValue && typeof numCropsValue === 'number' && numCropsValue > 0)) {
        console.log('Found Complete audit indicators (contact or crops) -> Complete Audit (Type 1)');
        return 1; // Complete audit
      }
    }
    
    // Strategy 3: Check third worksheet structure
    // In Complete audits, third sheet is "Datos Generales Tango"
    // In Yield audits, third sheet is a plot (starts with P or I)
    if (workbook.worksheets.length > 2) {
      const thirdSheet = workbook.worksheets[2];
      const thirdSheetName = thirdSheet.name.trim();
      
      // If third sheet name starts with P or I followed by numbers, it's likely a plot (Yield audit)
      if (/^[PI]\d+/.test(thirdSheetName)) {
        console.log(`Third sheet is "${thirdSheetName}" (plot name) -> Yield Audit (Type 2)`);
        return 2; // Yield audit
      }
    }
    
    // Default to Yield audit if we can't determine
    console.log('Could not determine audit type definitively, defaulting to Yield Audit (Type 2)');
    return 2;
  }

  /**
   * Helper: Find worksheet by multiple possible names (supports Spanish and Italian)
   */
  private findWorksheetByNames(workbook: ExcelJS.Workbook, possibleNames: string[]): ExcelJS.Worksheet | undefined {
    // Try exact match first
    for (const name of possibleNames) {
      const ws = workbook.getWorksheet(name);
      if (ws) {
        console.log(`Found worksheet with exact match: "${ws.name}" for search: ${possibleNames.join(', ')}`);
        return ws;
      }
    }

    // Try case-insensitive match
    for (const name of possibleNames) {
      const ws = workbook.worksheets.find(
        w => w.name.toLowerCase().trim() === name.toLowerCase().trim()
      );
      if (ws) {
        console.log(`Found worksheet with case-insensitive match: "${ws.name}" for search: ${name}`);
        return ws;
      }
    }

    // Try partial match
    for (const name of possibleNames) {
      const ws = workbook.worksheets.find(w => {
        const wsName = w.name.toLowerCase().trim();
        const searchName = name.toLowerCase().trim();
        return wsName.includes(searchName) || searchName.includes(wsName);
      });
      if (ws) {
        console.log(`Found worksheet with partial match: "${ws.name}" for search: ${name}`);
        return ws;
      }
    }

    console.warn(`Could not find worksheet with any of these names: ${possibleNames.join(', ')}`);
    return undefined;
  }

  /**
   * Helper: Get the actual value from a cell (handles formulas)
   */
  private getCellValue(cell: ExcelJS.Cell): any {
    // If cell has a formula, get the result
    if (cell.formula || cell.type === ExcelJS.ValueType.Formula) {
      return cell.result;
    }
    return cell.value;
  }

  /**
   * Helper: Get numeric value from cell
   */
  private getNumValue(
    worksheet: ExcelJS.Worksheet,
    row: number,
    column: number
  ): number {
    const cell = worksheet.getCell(row, column);
    const value = this.getCellValue(cell);
    if (!value || value === 'NAP') {
      return 0;
    }
    return typeof value === 'number' ? value : 0;
  }

  /**
   * Helper: Get float value from cell
   */
  private getFloatValue(
    worksheet: ExcelJS.Worksheet,
    row: number,
    column: number
  ): number {
    const cell = worksheet.getCell(row, column);
    const value = this.getCellValue(cell);
    if (!value || value === 'NAP') {
      return 0.0;
    }
    return typeof value === 'number' ? value : 0.0;
  }

  /**
   * Helper: Get string value from cell
   */
  private getStrValue(
    worksheet: ExcelJS.Worksheet,
    row: number,
    column: number
  ): string {
    const cell = worksheet.getCell(row, column);
    const value = this.getCellValue(cell);
    if (!value || value === 'NAP' || value === 'NONE') {
      return '';
    }
    if (typeof value === 'string') {
      return value;
    }
    return String(value);
  }

  /**
   * Helper: Get boolean value from cell (supports SI/YES/SÍ in any case)
   */
  private getBooleanValue(
    worksheet: ExcelJS.Worksheet,
    row: number,
    column: number
  ): boolean {
    const cell = worksheet.getCell(row, column);
    const value = this.getCellValue(cell);
    if (!value) return false;
    const strValue = String(value).toUpperCase().trim();
    return strValue === 'SI' || strValue === 'SÍ' || strValue === 'YES' || strValue === 'SÌ';
  }

  /**
   * Helper: Normalize eco field value (Italian/Spanish to standard format)
   * convenzionale/Convencional -> Convencional
   * in conversione/En conversión -> En conversión
   * BIO/Ecológico -> Ecológico
   */
  private normalizeEcoField(value: string): string {
    if (!value) return '';
    const normalized = value.toUpperCase().trim();
    
    if (normalized === 'CONVENZIONALE' || normalized === 'CONVENCIONAL') {
      return 'Convencional';
    }
    if (normalized === 'IN CONVERSIONE' || normalized === 'EN CONVERSIÓN' || normalized === 'EN CONVERSION') {
      return 'En conversión';
    }
    if (normalized === 'BIO' || normalized === 'ECOLÓGICO' || normalized === 'ECOLOGICO' || normalized === 'BIOLOGICO') {
      return 'Ecológico';
    }
    
    // Return original if no match
    return value;
  }

  /**
   * Helper: Format date to string
   */
  private formatDate(value: any): string {
    if (!value) return '';
    if (value instanceof Date) {
      return value.toISOString().split('T')[0];
    }
    return String(value);
  }

  /**
   * Helper: Format time to string
   */
  private formatTime(value: any): string {
    if (!value) return '';
    if (value instanceof Date) {
      return value.toTimeString().split(' ')[0];
    }
    return String(value);
  }

  /**
   * Helper: Convert year to plantation date (January 1st of that year)
   * @param year - Year as number (e.g., 2017)
   * @returns Date string in format YYYY-01-01
   */
  private yearToPlantationDate(year: number): string {
    if (!year || year === 0) return '';
    // Ensure year is a valid 4-digit year
    const yearNum = Math.floor(year);
    if (yearNum < 1900 || yearNum > 2100) return '';
    return `${yearNum}-01-01`;
  }

  /**
   * Reads an Excel file and returns the workbook
   * @param file - The Excel file to read
   * @returns Promise with the workbook
   */
  async readExcelFile(file: File): Promise<ExcelJS.Workbook> {
    try {
      const arrayBuffer = await file.arrayBuffer();
      const workbook = new ExcelJS.Workbook();
      await workbook.xlsx.load(arrayBuffer);
      return workbook;
    } catch (error) {
      throw new Error(
        `Error reading Excel file: ${error instanceof Error ? error.message : 'Unknown error'}`
      );
    }
  }

  /**
   * Extracts audit reference code from producer data worksheet
   * @param workbook - The Excel workbook
   * @param auditType - Type of audit (1 = Complete, 2 = Yield)
   * @returns Audit reference code
   */
  private extractAuditReference(workbook: ExcelJS.Workbook, auditType: number): string {
    // Try to find producer data worksheet by name (Spanish or Italian)
    let ws = this.findWorksheetByNames(workbook, [
      'Datos Productor',
      'Datos Generales Productor',
      'Dati generali Produttore',
      'Dati Produttore',
      'Producer Data',
      'General Producer Data'
    ]);
    
    // Fallback to second worksheet
    if (!ws) {
      console.warn('Using second worksheet as fallback for producer data');
      ws = workbook.worksheets[1];
    }
    
    if (!ws) {
      return '';
    }
    
    console.log(`Reading audit reference from worksheet: "${ws.name}"`);

    // Both complete and yield audits have the code in row 3, column 2 (B3)
    const codeRow = auditType === 1 ? AUDIT_CODE_COMPLETE_ROW : AUDIT_CODE_YIELD_ROW;
    const reference = this.getStrValue(ws, codeRow, 2);
    
    console.log(`Extracted audit reference from producer sheet B${codeRow}: "${reference}"`);
    return reference;
  }

  /**
   * Extracts general audit data from the first worksheet
   * @param workbook - The Excel workbook
   * @param auditType - Type of audit (1 = Complete, 2 = Yield) - needed to extract reference
   * @returns Audit general data
   * 
   * Note: contact_receiver data (B6-B9) should be matched with producer contacts in backend.
   * If contact doesn't exist, it should be created and associated.
   */
  parseAuditData(workbook: ExcelJS.Workbook, auditType?: number): AuditGeneralData {
    // Try to find audit data worksheet by name (Spanish or Italian)
    let ws = this.findWorksheetByNames(workbook, [
      'Datos Auditoria',
      'Datos Auditoría',
      'Data Audit',
      'Dati Audit',
      'Audit Data'
    ]);
    
    // Fallback to first worksheet
    if (!ws) {
      console.warn('Using first worksheet as fallback for audit data');
      ws = workbook.worksheets[0];
    }
    
    if (!ws) {
      throw new Error('No worksheet found in workbook');
    }
    
    console.log(`Reading audit data from worksheet: "${ws.name}"`);

    // Extract audit reference from producer data sheet if audit type is provided
    const reference = auditType ? this.extractAuditReference(workbook, auditType) : undefined;

    return {
      reference,
      start_date: this.formatDate(ws.getCell(DATE_AUDIT_ROW, 2).value),
      start_time: this.formatTime(ws.getCell(START_TIME_ROW, 2).value),
      finishing_time: this.formatTime(ws.getCell(FINISH_TIME_ROW, 2).value),
      contact_receiver: this.getStrValue(
        ws,
        PERSON_IN_CHARGE_OF_RECEIVING_ROW,
        2
      ),
      contact_receiver_phone: this.getStrValue(
        ws,
        PHONE_PERSON_IN_CHARGE_ROW,
        2
      ),
      contact_receiver_nif: this.getStrValue(
        ws,
        NIF_PERSON_IN_CHARGE_RECEIVING_ROW,
        2
      ),
      position: this.getStrValue(ws, ROLE_IN_COMPANY_PERSON_IN_CHARGE_ROW, 2),
      auditor: this.getStrValue(ws, AUDITOR_ROW, 2),
      nif_auditor: this.getStrValue(ws, AUDITOR_NIF_ROW, 2),
      phone_auditor: this.getStrValue(ws, AUDITOR_PHONE_ROW, 2),
      comments: this.getStrValue(ws, COMMENTS_ROW, 1),
    };
  }

  /**
   * Extracts complete producer data from the second worksheet
   * @param workbook - The Excel workbook
   * @returns Producer data
   * 
   * Note: Backend should handle:
   * - packer (B10): Match with existing packer or create new
   * - exporter (B11): Match with existing exporter or create new
   * - decrease_reason (B19): Match with existing reason or create new
   * - under_net (B20): Normalized from SI/NO/Si/No/si/no to 1/0
   * - eco_field (B21): Normalized from Italian (convenzionale/in conversione/BIO) to Spanish
   */
  private parseCompleteProducerData(workbook: ExcelJS.Workbook): ProducerData {
    // Try to find producer data worksheet by name (Spanish or Italian)
    let ws = this.findWorksheetByNames(workbook, [
      'Datos Productor',
      'Datos Generales Productor',
      'Dati generali Produttore',
      'Dati Produttore',
      'Producer Data',
      'General Producer Data'
    ]);
    
    // Fallback to second worksheet
    if (!ws) {
      console.warn('Using second worksheet as fallback for producer data');
      ws = workbook.worksheets[1];
    }
    
    if (!ws) {
      throw new Error('Producer data worksheet not found');
    }
    
    console.log(`Reading complete producer data from worksheet: "${ws.name}"`);

    const crops: CropData[] = [];
    let row = NUMBER_OF_CROPS_COMPLETE_ROW;
    const varietiesCounter = this.getNumValue(
      ws,
      NUMBER_OF_CROPS_COMPLETE_ROW,
      2
    );
    for (let i = 1; i <= varietiesCounter; i++) {
      crops.push({
        variety_nearer_type: this.getStrValue(ws, row + 1, 2),
        surface: this.getNumValue(ws, row + 2, 2),
      });
      row += 2;
    }

    const controlPoints: ControlPointData[] = [];
    const samples: SampleData[] = [];
    row = NAME_FIRST_CONTROL_POINT_COMPLETE_ROW;
    const controlPointsCounter = this.getNumValue(
      ws,
      NUMBER_OF_CONTROL_POINTS_COMPLETE_ROW,
      2
    );
    for (let i = 1; i <= controlPointsCounter; i++) {
      const cpName = this.getStrValue(ws, row, 2);
      const controlPoint: ControlPointData = {
        name: cpName,
        is_owner: this.getBooleanValue(ws, row + 1, 2),
        reason: this.getStrValue(ws, row + 2, 2),
        samples: [],
      };

      const counterSamples = this.getNumValue(ws, row + 3, 2);
      for (let j = 1; j <= counterSamples; j++) {
        const sample: SampleData = {
          plot_name: cpName,
          code: this.getStrValue(ws, row + 3 + j, 2).toUpperCase(),
          is_control_point: true,
          position_waypoint: this.getStrValue(ws, row + 3 + j, 3),
        };
        controlPoint.samples.push(sample);
        samples.push(sample);
      }

      controlPoints.push(controlPoint);
      row += 12;
    }

    return {
      farm_name: this.getStrValue(ws, FIELD_NAME_COMPLETE_ROW, 2),
      packer: this.getStrValue(ws, EXPORTER_COMPLETE_ROW, 2),
      exporter: this.getStrValue(ws, PACK_HOUSE_COMPLETE_ROW, 2),
      contact: this.getStrValue(ws, CONTACT_COMPLETE_ROW, 2),
      phone: this.getStrValue(ws, PHONE_CONTACT_COMPLETE_ROW, 2),
      email: this.getStrValue(ws, EMAIL_CONTACT_COMPLETE_ROW, 2),
      farm_surface: this.getNumValue(ws, TOTAL_SURFACE_COMPLETE_ROW, 2),
      map_is_showing_reality: this.getBooleanValue(
        ws,
        CHECK_MAP_COMPLETE_ROW,
        2
      ),
      stimated_production: this.getNumValue(
        ws,
        ESTIMATED_YIELD_COMPLETE_ROW,
        2
      ),
      harvest_date: this.formatDate(
        ws.getCell(HARVEST_DATE_COMPLETE_ROW, 2).value
      ),
      decrease_percentage: this.getNumValue(
        ws,
        PERCENTAGE_DECREASE_COMPLETE_ROW,
        2
      ),
      decrease_reason: this.getStrValue(ws, REASON_DECREASE_COMPLETE_ROW, 2),
      under_net: this.getBooleanValue(ws, UNDERCOVER_FIELD, 2) ? 'si' : 'no',
      eco_field: this.normalizeEcoField(this.getStrValue(ws, ECO_FIELD, 2)),
      crops,
      control_points: controlPoints,
      samples,
    };
  }

  /**
   * Extracts yield producer data from the second worksheet
   * @param workbook - The Excel workbook
   * @returns Producer data
   * 
   * Note: Backend should handle:
   * - packer (B10): Match with existing packer or create new
   * - exporter (B11): Match with existing exporter or create new
   * - decrease_reason (B15): Match with existing reason or create new
   * - under_net (B16): Normalized from SI/NO/Si/No/si/no to 1/0
   * - eco_field (B17): Normalized from Italian (convenzionale/in conversione/BIO) to Spanish
   */
  private parseYieldProducerData(workbook: ExcelJS.Workbook): ProducerData {
    // Try to find producer data worksheet by name (Spanish or Italian)
    let ws = this.findWorksheetByNames(workbook, [
      'Datos Productor',
      'Datos Generales Productor',
      'Dati generali Produttore',
      'Dati Produttore',
      'Producer Data',
      'General Producer Data'
    ]);
    
    // Fallback to second worksheet
    if (!ws) {
      console.warn('Using second worksheet as fallback for producer data');
      ws = workbook.worksheets[1];
    }
    
    if (!ws) {
      throw new Error('Producer data worksheet not found');
    }
    
    console.log(`Reading yield producer data from worksheet: "${ws.name}"`);

    const controlPoints: ControlPointData[] = [];
    const samples: SampleData[] = [];
    let row = NUMBER_OF_CONTROL_POINTS_YIELD_ROW + 2;
    const controlPointsCounter = this.getNumValue(
      ws,
      NUMBER_OF_CONTROL_POINTS_YIELD_ROW,
      2
    );
    for (let i = 1; i <= controlPointsCounter; i++) {
      const cpName = this.getStrValue(ws, row, 2);
      const controlPoint: ControlPointData = {
        name: cpName,
        is_owner: this.getBooleanValue(ws, row + 1, 2),
        reason: this.getStrValue(ws, row + 2, 2),
        samples: [],
      };

      const counterSamples = this.getNumValue(ws, row + 3, 2);
      for (let j = 1; j <= counterSamples; j++) {
        const sample: SampleData = {
          plot_name: cpName,
          code: this.getStrValue(ws, row + 3 + j, 2).toUpperCase(),
          is_control_point: true,
          position_waypoint: this.getStrValue(ws, row + 3 + j, 3),
        };
        controlPoint.samples.push(sample);
        samples.push(sample);
      }

      controlPoints.push(controlPoint);
      row += 9;
    }

    return {
      farm_name: this.getStrValue(ws, FIELD_NAME_YIELD_ROW, 2),
      packer: this.getStrValue(ws, EXPORTER_YIELD_ROW, 2),
      exporter: this.getStrValue(ws, PACK_HOUSE_YIELD_ROW, 2),
      farm_surface: 0,
      map_is_showing_reality: false,
      stimated_production: this.getNumValue(ws, ESTIMATED_YIELD_YIELD_ROW, 2),
      harvest_date: this.formatDate(
        ws.getCell(HARVEST_DATE_YIELD_ROW, 2).value
      ),
      decrease_percentage: this.getNumValue(ws, PERCENTAGE_LOOSE_YIELD_ROW, 2),
      decrease_reason: this.getStrValue(ws, REASON_LOOSE_YIELD_ROW, 2),
      under_net: this.getBooleanValue(ws, UNDERCOVER_FIELD_YIELD, 2) ? 'si' : 'no',
      eco_field: this.normalizeEcoField(this.getStrValue(ws, ECO_FIELD_YIELD, 2)),
      crops: [],
      control_points: controlPoints,
      samples,
    };
  }

  /**
   * Extracts producer data from the second worksheet based on audit type
   * @param workbook - The Excel workbook
   * @param auditType - Type of audit (1 = Complete, 2 = Yield)
   * @returns Producer data
   */
  parseProducerData(
    workbook: ExcelJS.Workbook,
    auditType: number
  ): ProducerData {
    if (auditType === 1) {
      return this.parseCompleteProducerData(workbook);
    } else if (auditType === 2) {
      return this.parseYieldProducerData(workbook);
    } else {
      throw new Error(`Invalid audit type: ${auditType}`);
    }
  }

  /**
   * Extracts complete plot data from worksheets 3+
   * @param workbook - The Excel workbook
   * @returns Array of plot data
   */
  private parseCompletePlotsData(workbook: ExcelJS.Workbook): PlotData[] {
    const plots: PlotData[] = [];
    
    // Debug: List all worksheets
    console.log('All worksheets in workbook:');
    workbook.worksheets.forEach((ws, idx) => {
      console.log(`  [${idx}] "${ws.name}"`);
    });
    
    // Try to find "Datos Generales Tango" worksheet by name (Spanish or Italian)
    let ws = this.findWorksheetByNames(workbook, [
      'Datos Generales Tango',
      'Datos Parcelas',
      'Dati Generali Tango',
      'Dati Parcelle',
      'General Tango Data',
      'Plot Data'
    ]);
    
    // If still not found, fall back to third worksheet (index 2)
    if (!ws) {
      console.warn('Could not find plots summary worksheet, falling back to index 2');
      ws = workbook.worksheets[2];
      if (ws) {
        console.log(`Using fallback worksheet at index 2: "${ws.name}"`);
      }
    }
    
    if (!ws) {
      console.warn('No worksheet found for plot data (tried "Datos Generales Tango" and index 2)');
      return plots;
    }
    
    console.log(`Reading plot data from worksheet: ${ws.name}`);

    // Debug: Check what's in cell B3
    const cellB3 = ws.getCell(NUMBER_PLOTS_TANGO_ROW, 2);
    const cellB3Value = this.getCellValue(cellB3);
    console.log(`Cell B${NUMBER_PLOTS_TANGO_ROW}:`, {
      formula: cellB3.formula,
      rawValue: cellB3.value,
      result: cellB3.result,
      calculatedValue: cellB3Value,
      type: typeof cellB3Value
    });
    
    const numberOfPlots = this.getNumValue(ws, NUMBER_PLOTS_TANGO_ROW, 2);
    console.log(`Number of plots detected in cell B${NUMBER_PLOTS_TANGO_ROW}: ${numberOfPlots}`);
    
    if (numberOfPlots === 0) {
      console.warn('No plots detected. Check if cell B3 contains the number of plots.');
      console.log('First 10 rows of column B:');
      for (let i = 1; i <= 10; i++) {
        const cell = ws.getCell(i, 2);
        const val = this.getCellValue(cell);
        console.log(`  B${i}: ${val} (type: ${typeof val}, formula: ${cell.formula || 'none'})`);
      }
    }
    
    const plotNames: string[] = [];
    // Read plot names from row 4, columns B, C, D, etc. (columns 2, 3, 4, ...)
    for (let i = 0; i < numberOfPlots; i++) {
      const columnIndex = 2 + i; // Start from column B (2), then C (3), D (4), etc.
      const plotName = this.getStrValue(ws, NAME_OF_PLOTS_COMPLETE_ROW, columnIndex);
      console.log(`Reading plot name from row ${NAME_OF_PLOTS_COMPLETE_ROW}, column ${columnIndex} (${String.fromCharCode(64 + columnIndex)}): "${plotName}"`);
      if (plotName) {
        plotNames.push(plotName);
      }
    }
    console.log(`Plot names found: ${plotNames.join(', ')}`);

    // Get all worksheet names for debugging
    const worksheetNames = workbook.worksheets.map(w => w.name);
    console.log(`Available worksheets: ${worksheetNames.join(', ')}`);

    for (const plotName of plotNames) {
      // Try exact match first
      let plotWs = workbook.getWorksheet(plotName);
      
      // If not found, try case-insensitive match
      if (!plotWs) {
        const matchingWs = workbook.worksheets.find(
          w => w.name.toLowerCase() === plotName.toLowerCase()
        );
        if (matchingWs) {
          plotWs = matchingWs;
          console.log(`Found plot worksheet with case-insensitive match: ${matchingWs.name} for ${plotName}`);
        }
      }
      
      // If still not found, try trimmed match
      if (!plotWs) {
        const matchingWs = workbook.worksheets.find(
          w => w.name.trim().toLowerCase() === plotName.trim().toLowerCase()
        );
        if (matchingWs) {
          plotWs = matchingWs;
          console.log(`Found plot worksheet with trimmed match: ${matchingWs.name} for ${plotName}`);
        }
      }
      
      if (!plotWs) {
        console.warn(`Plot worksheet not found for: "${plotName}"`);
        continue;
      }

      const plantation = this.getStrValue(
        plotWs,
        PLANTATION_TYPE_COMPLETE_ROW,
        2
      ).toUpperCase();
      const plantationType =
        plantation !== 'INJERTA' && plantation !== 'GRAFTED' ? 1 : 2;
      const graftingType = plantationType === 2 ? 1 : 5;

      const blocks: BlockData[] = [];
      const kgPlot = this.getNumValue(plotWs, YIELD_COMPLETE_ROW, 2);
      if (kgPlot > 0) {
        let row = START_BLOCK_COMPLETE_ROW;
        let amountPercentage = 0;
        for (let j = 1; j <= 5; j++) {
          const blockPercentage = this.getNumValue(plotWs, row, 2);
          if (blockPercentage > 0) {
            blocks.push({
              percentage: blockPercentage,
              avg_yield: this.getNumValue(plotWs, row, 3),
              avg_caliber: this.getNumValue(plotWs, row, 4),
            });
          }
          row += 1;
          amountPercentage += blockPercentage;
          if (amountPercentage >= 100) break;
        }
      }

      const samples: SampleData[] = [];
      let row = NUMBER_OF_SAMPLES_COMPLETE_ROW;
      const counterSamples = this.getNumValue(plotWs, row, 2);
      for (let j = 1; j <= counterSamples; j++) {
        samples.push({
          plot_name: plotName,
          code: this.getStrValue(plotWs, row + j, 2).toUpperCase(),
          is_control_point: false,
          position_waypoint: this.getStrValue(plotWs, row + j, 3),
        });
      }

      plots.push({
        name: this.getStrValue(plotWs, PLOT_NAME_COMPLETE_ROW, 2),
        plot_type_id: plantationType,
        pattern: this.getStrValue(plotWs, ROOTSTOCK_COMPLETE_ROW, 2),
        surface: this.getNumValue(plotWs, SURFACE_COMPLETE_ROW, 2),
        plantation_frame_row: this.getNumValue(
          plotWs,
          SPACING_ROW_COMPLETE_ROW,
          2
        ),
        plantation_frame_column: this.getNumValue(
          plotWs,
          SPACING_COLUMN_COMPLETE_ROW,
          2
        ),
        plantation_date: this.formatDate(
          plotWs.getCell(CROP_DATE_COMPLETE_ROW, 2).value
        ),
        crop_date: new Date(
          plotWs.getCell(CROP_DATE_COMPLETE_ROW, 2).value as any
        ).getFullYear(),
        plants: this.getNumValue(plotWs, NUM_PLANTS_COMPLETE_ROW, 2),
        intermediate_wood: this.getStrValue(plotWs, WOODSTOCK_COMPLETE_ROW, 2),
        grafting_type: graftingType,
        grafting: this.getNumValue(plotWs, NUM_GRAFTS_COMPLETE_ROW, 2),
        doubled_with: this.getStrValue(plotWs, DOUBLED_WITH_COMPLETE_ROW, 2),
        kg_plot: kgPlot,
        avg_caliber_calculated: this.getNumValue(
          plotWs,
          AVG_CALIBER_COMPLETE_ROW,
          2
        ),
        brix: this.getNumValue(plotWs, BRIX_COMPLETE_ROW, 2),
        acidity: this.getNumValue(plotWs, ACIDITY_COMPLETE_ROW, 2),
        is_ilegal: this.getBooleanValue(plotWs, ILLEGAL_PLOT_COMPLETE_ROW, 2),
        external_damage: this.getBooleanValue(
          plotWs,
          EXTERNAL_DAMAGE_COMPLETE_ROW,
          2
        ),
        blocks,
        samples,
      });
    }

    return plots;
  }

  /**
   * Extracts yield plot data from worksheets 3+
   * @param workbook - The Excel workbook
   * @returns Array of plot data
   */
  private parseYieldPlotsData(workbook: ExcelJS.Workbook): PlotData[] {
    const plots: PlotData[] = [];
    const counterSheets = workbook.worksheets.length;

    console.log(`Parsing yield plots. Total worksheets: ${counterSheets}`);
    console.log('Worksheets:', workbook.worksheets.map((ws, idx) => `[${idx}] ${ws.name}`).join(', '));

    for (let i = 2; i < counterSheets; i++) {
      const ws = workbook.worksheets[i];
      if (!ws) {
        console.warn(`Worksheet at index ${i} is undefined`);
        continue;
      }

      console.log(`Processing yield plot from worksheet [${i}] "${ws.name}"`);
      const plotName = this.getStrValue(ws, PLOT_NAME_YIELD_ROW, 2);
      console.log(`Plot name from B${PLOT_NAME_YIELD_ROW}: "${plotName}"`);
      const firstChar = plotName.charAt(0).toUpperCase();
      const plantationType = firstChar === 'P' ? 1 : 2;
      const graftingType = plantationType === 2 ? 1 : 5;

      // Read avg_caliber from D22 (first block row, column D) - applies to ALL blocks
      const avgCaliberForAllBlocks = this.getNumValue(ws, START_BLOCK_YIELD, 4);
      console.log(`Plot ${plotName}: avg_caliber from D22 = ${avgCaliberForAllBlocks} (will be applied to all blocks)`);

      const blocks: BlockData[] = [];
      let row = START_BLOCK_YIELD;
      let amountPercentage = 0;
      for (let j = 1; j <= 5; j++) {
        const blockPercentage = this.getNumValue(ws, row, 2);
        if (blockPercentage > 0) {
          const avgYield = this.getNumValue(ws, row, 3);
          blocks.push({
            percentage: blockPercentage,
            avg_yield: avgYield,
            avg_caliber: avgCaliberForAllBlocks, // Same caliber for all blocks from D22
          });
          console.log(`  Block ${j}: percentage=${blockPercentage}%, avg_yield=${avgYield}, avg_caliber=${avgCaliberForAllBlocks}`);
        }
        row += 1;
        amountPercentage += blockPercentage;
        if (amountPercentage >= 100) break;
      }
      console.log(`Plot ${plotName}: ${blocks.length} blocks extracted with shared avg_caliber=${avgCaliberForAllBlocks}`);

      const samples: SampleData[] = [];
      row = NUMBER_OF_SAMPLES_YIELD_ROW;
      const counterSamples = this.getNumValue(ws, row, 2);
      for (let j = 1; j <= counterSamples; j++) {
        samples.push({
          plot_name: this.getStrValue(ws, PLOT_NAME_YIELD_ROW, 2),
          code: this.getStrValue(ws, row + j, 2).toUpperCase(),
          is_control_point: false,
          position_waypoint: this.getStrValue(ws, row + j, 3),
        });
      }

      const frame = this.getStrValue(ws, SPACING_YIELD_ROW, 2);
      const frameParts = frame.split('X');
      const frameRow =
        frameParts.length > 0 ? parseFloat(frameParts[0].replace(',', '.')) : 0;
      const frameColumn =
        frameParts.length > 1 ? parseFloat(frameParts[1].replace(',', '.')) : 0;

      // Get crop year and convert to plantation date (January 1st of that year)
      const cropYear = this.getNumValue(ws, CROP_YEAR_YIELD_ROW, 2);
      const plantationDate = this.yearToPlantationDate(cropYear);
      console.log(`Plot ${plotName}: crop_year=${cropYear}, plantation_date=${plantationDate}`);

      plots.push({
        name: plotName,
        plot_type_id: plantationType,
        pattern: this.getStrValue(ws, ROOTSTOCK_YIELD_ROW, 2),
        surface: this.getNumValue(ws, SURFACE_YIELD_ROW, 2),
        plantation_frame_row: frameRow,
        plantation_frame_column: frameColumn,
        plantation_date: plantationDate, // Converted from year to date
        crop_date: cropYear, // Keep the year as well
        plants: this.getNumValue(ws, NUM_PLANTS_YIELD_ROW, 2),
        intermediate_wood: this.getStrValue(ws, WOODSTOCK_YIELD_ROW, 2),
        grafting_type: graftingType,
        grafting: this.getNumValue(ws, NUM_GRAFTS_YIELD_ROW, 2),
        doubled_with: this.getStrValue(ws, DOUBLED_WITH_YIELD_ROW, 2),
        kg_plot: this.getNumValue(ws, YIELD_ROW, 2),
        avg_caliber_calculated: this.getNumValue(ws, AVG_CALIBER_YIELD_ROW, 2),
        brix: this.getNumValue(ws, BRIX_YIELD_ROW, 2),
        acidity: this.getNumValue(ws, ACIDITY_YIELD_ROW, 2),
        is_ilegal: false,
        external_damage: this.getBooleanValue(ws, EXTERNAL_DAMAGE_YIELD_ROW, 2),
        blocks,
        samples,
      });
    }

    return plots;
  }

  /**
   * Extracts plot data from worksheets 3+ based on audit type
   * @param workbook - The Excel workbook
   * @param auditType - Type of audit (1 = Complete, 2 = Yield)
   * @returns Array of plot data
   */
  parsePlotsData(workbook: ExcelJS.Workbook, auditType: number): PlotData[] {
    if (auditType === 1) {
      return this.parseCompletePlotsData(workbook);
    } else if (auditType === 2) {
      return this.parseYieldPlotsData(workbook);
    } else {
      throw new Error(`Invalid audit type: ${auditType}`);
    }
  }

  /**
   * Validates that required fields are present in the data
   * @param data - The data to validate (should contain audit_data, producer_data, plots_data)
   * @param auditType - Type of audit (1 = Complete, 2 = Yield)
   * @returns Validation result with errors if any
   */
  validateRequiredFields(data: any, auditType: number): ValidationResult {
    const errors: string[] = [];

    // Validate audit data
    if (!data.audit_data) {
      errors.push('Missing audit data');
    } else {
      const auditData = data.audit_data;
      if (!auditData.start_date) errors.push('Missing start date');
      if (!auditData.start_time) errors.push('Missing start time');
      if (!auditData.finishing_time) errors.push('Missing finishing time');
      if (!auditData.contact_receiver)
        errors.push('Missing contact receiver name');
      if (!auditData.position) errors.push('Missing position');
    }

    // Validate producer data
    if (!data.producer_data) {
      errors.push('Missing producer data');
    } else {
      const producerData = data.producer_data;
      if (!producerData.farm_name) errors.push('Missing farm name');
      // packer and exporter are optional
      if (producerData.stimated_production === undefined)
        errors.push('Missing estimated production');

      if (auditType === 1) {
        // Complete audit specific validations
        if (producerData.farm_surface === undefined)
          errors.push('Missing farm surface');
        if (producerData.map_is_showing_reality === undefined)
          errors.push('Missing map reality check');
        // crops is optional - can be empty array
      }

      if (auditType === 2) {
        // Yield audit specific validations
        if (!producerData.harvest_date) errors.push('Missing harvest date');
      }
    }

    // Validate plots data
    if (!data.plots_data || !Array.isArray(data.plots_data)) {
      errors.push('Missing or invalid plots data');
    } else if (data.plots_data.length === 0) {
      errors.push('No plots found in data');
    } else {
      data.plots_data.forEach((plot: any, index: number) => {
        if (!plot.name) errors.push(`Plot ${index + 1}: Missing name`);
        if (plot.surface === undefined || plot.surface <= 0)
          errors.push(`Plot ${index + 1}: Invalid surface`);
      });
    }

    return {
      valid: errors.length === 0,
      errors,
    };
  }

  /**
   * Converts a file to Base64 string
   * @param file - The file to convert
   * @returns Promise with Base64 encoded string
   */
  async fileToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = () => {
        try {
          const result = reader.result as string;
          // Remove the data URL prefix (e.g., "data:application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;base64,")
          const base64 = result.split(',')[1];
          resolve(base64);
        } catch (error) {
          reject(
            new Error(
              `Error encoding file to Base64: ${error instanceof Error ? error.message : 'Unknown error'}`
            )
          );
        }
      };

      reader.onerror = () => {
        reject(new Error('Error reading file for Base64 encoding'));
      };

      reader.readAsDataURL(file);
    });
  }
}
